import React, { useState } from 'react';
import { AlertTriangle, RotateCcw, Trash2, Home, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ERROR_MESSAGES } from '@/utils/error-screen-messages';

interface UnifiedErrorScreenProps {
  variant: 'initialization' | 'runtime' | 'component';
  error?: Error;
  errorInfo?: React.ErrorInfo;
  onRetry?: () => void;
  showHomeButton?: boolean;
}

export const UnifiedErrorScreen: React.FC<UnifiedErrorScreenProps> = ({
  variant,
  error,
  errorInfo,
  onRetry,
  showHomeButton = true,
}) => {
  const [isClearing, setIsClearing] = useState(false);
  
  // Always use English
  const messages = ERROR_MESSAGES[variant].en;

  const handleClearCacheAndReload = async () => {
    setIsClearing(true);
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Clear localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      
      // Hard reload
      window.location.reload();
    } catch (err) {
      console.error('Failed to clear cache:', err);
      // Fallback: just reload
      window.location.reload();
    }
  };

  return (
    <div 
      className="flex items-center justify-center min-h-screen p-6"
      style={{
        background: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--muted) / 0.3) 100%)',
      }}
    >
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <img 
            src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png"
            alt="Trade Bot 365"
            className="h-16 w-auto"
            onError={(e) => {
              // Fallback if logo fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        {/* Error Card */}
        <div 
          className="rounded-xl border-2 p-10 shadow-2xl backdrop-blur-md animate-fade-in"
          style={{
            background: 'hsl(var(--card) / 0.95)',
            borderColor: 'hsl(var(--border) / 0.5)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Animated Icon */}
          <div className="flex justify-center mb-8">
            <div 
              className="flex items-center justify-center w-24 h-24 rounded-full animate-bounce-subtle"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--destructive) / 0.1) 0%, hsl(var(--destructive) / 0.2) 100%)',
                boxShadow: '0 8px 24px hsl(var(--destructive) / 0.3)',
              }}
            >
              <AlertTriangle 
                className="w-12 h-12"
                style={{ color: 'hsl(var(--destructive))' }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="text-center space-y-5 mb-10">
            <h1 
              className="text-4xl font-bold tracking-tight"
              style={{ color: 'hsl(var(--foreground))' }}
            >
              {messages.title}
            </h1>
            
            <p 
              className="text-lg leading-relaxed max-w-md mx-auto"
              style={{ color: 'hsl(var(--muted-foreground))' }}
            >
              {messages.description}
            </p>
            
            {messages.hint && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" 
                     style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                  <span className="text-lg">💡</span>
                </div>
                <p 
                  className="text-sm font-medium"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                >
                  {messages.hint}
                </p>
              </div>
            )}
          </div>

          {/* Visual Separator */}
          <div className="w-full h-px mb-8" style={{ background: 'hsl(var(--border) / 0.5)' }} />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {onRetry && (
              <Button 
                onClick={onRetry} 
                variant="outline" 
                size="lg"
                disabled={isClearing}
                className="min-w-[160px] hover:scale-105 transition-transform"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            
            <Button 
              onClick={handleClearCacheAndReload} 
              variant="destructive" 
              size="lg"
              disabled={isClearing}
              className="min-w-[200px] hover:scale-105 transition-transform"
            >
              {isClearing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Cache & Reload
                </>
              )}
            </Button>
            
            {showHomeButton && variant === 'component' && (
              <Button 
                asChild
                variant="ghost" 
                size="lg"
                disabled={isClearing}
                className="min-w-[160px] hover:scale-105 transition-transform"
              >
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Return Home
                </Link>
              </Button>
            )}
          </div>

          {/* Progress Indicator */}
          {isClearing && (
            <div className="mt-8 p-4 rounded-lg animate-fade-in" 
                 style={{ background: 'hsl(var(--primary) / 0.1)' }}>
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'hsl(var(--primary))' }} />
                <span className="text-sm font-medium" style={{ color: 'hsl(var(--primary))' }}>
                  Clearing cache and reloading app...
                </span>
              </div>
            </div>
          )}

          {/* Dev Error Details */}
          {process.env.NODE_ENV === 'development' && error && (
            <details 
              className="mt-8 p-4 rounded-lg text-left"
              style={{
                background: 'hsl(var(--muted) / 0.5)',
                borderColor: 'hsl(var(--border))',
              }}
            >
              <summary 
                className="cursor-pointer font-mono text-sm font-medium mb-2"
                style={{ color: 'hsl(var(--foreground))' }}
              >
                🔧 Error Details (Development Only)
              </summary>
              <pre 
                className="mt-3 text-xs overflow-auto p-3 rounded"
                style={{
                  background: 'hsl(var(--background))',
                  color: 'hsl(var(--muted-foreground))',
                  maxHeight: '300px',
                }}
              >
                <strong>Error:</strong> {error.toString()}
                {'\n\n'}
                <strong>Stack:</strong>
                {'\n'}
                {error.stack}
                {errorInfo?.componentStack && (
                  <>
                    {'\n\n'}
                    <strong>Component Stack:</strong>
                    {'\n'}
                    {errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
        </div>

        {/* Footer hint */}
        <div className="text-center mt-8 text-sm animate-fade-in" 
             style={{ color: 'hsl(var(--muted-foreground))' }}>
          <p className="font-medium">
            If the problem persists, please contact support.
          </p>
        </div>
      </div>
    </div>
  );
};
